import { type FunctionComponent } from 'react';
import { SEARCH_RESULT_LIMIT } from '../../../constants/search';
import { formatNumber, pluralize } from '../../../helpers/general-helper/general-helper';
import { useSearchResults } from '../../../hooks/apollo/search/search.hooks';
import { type ProductSearchResult } from '../../../types/search.types';
import { TextButton } from '../../buttons';

function isMinimumMatchFallback(results: ProductSearchResult | undefined) {
	return results?.queryStep === 'mm';
}

export const SearchHeaderTitle: FunctionComponent = () => {
	const { results, previousResults, loading, query, flagged, clearFacets, facets } = useSearchResults();
	const productSearchResult = previousResults?.products?.length ? previousResults : null;
	const searchResults = loading ? productSearchResult : results;
	const count = searchResults?.count || 0;
	const resultCount = `${formatNumber(Math.min(count, SEARCH_RESULT_LIMIT))}${count > SEARCH_RESULT_LIMIT ? '+' : ''}`;
	const hasSpellCorrection = Boolean(searchResults?.correctedSearchTerm);
	const isFallback = isMinimumMatchFallback(results);
	const queryText = searchResults?.correctedSearchTerm || query;

	if (flagged) {
		return (
			<h1 className="ma0 f3 fw6" data-automation="search-header-text">
				Search
			</h1>
		);
	}

	const resultsCount = (
		<>
			<span data-automation="results-count">{resultCount}</span> {pluralize('result', count)}
		</>
	);

	return (
		<div>
			<h1 className="ma0 f3 fw6" data-automation="search-header-text">
				{isFallback && <span className="fw3">No results for </span>}
				{queryText}
			</h1>
			<div className="mt1 f5 lh-title fw3" data-testid="search-header-message">
				{isFallback ? (
					facets.length !== 0 ? (
						<>
							Try removing filters below to <TextButton onClick={clearFacets}>see all results</TextButton>.
						</>
					) : (
						<>
							Showing {resultsCount} that {count !== 1 ? 'match' : 'matches'} fewer words
						</>
					)
				) : (
					<>
						{resultsCount}
						{hasSpellCorrection && (
							<span>
								. No results for <span className="theme-error fw4">{query}</span>.
							</span>
						)}
					</>
				)}
			</div>
		</div>
	);
};
